/**
 * apc-collections-highlight
 *
 * Lazy-loads the collection cards for the Collections highlight section. The
 * section renders skeleton placeholders server-side; once the element scrolls
 * near the viewport this fetches the real cards from
 * `/search?view=apc-collections-highlight&q=<handles>` and swaps them in.
 *
 * Plain custom element (no refs/events), mirroring
 * `recently-viewed-products-section.js` — it is a loader shell, not a
 * `Component`.
 */
class ApcCollectionsHighlight extends HTMLElement {
  /** @type {IntersectionObserver} */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#load();
    },
    { rootMargin: '0px 0px 400px 0px' },
  );

  /** Reload when settings change in the theme editor. */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'data-handles') {
        this.#loaded = false;
        this.#load();
        break;
      }
    }
  });

  /** @type {AbortController | null} */
  #activeFetch = null;
  #loaded = false;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    if (Shopify.designMode) {
      this.#mutationObserver.observe(this, { attributes: true });
    }
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#activeFetch?.abort();
  }

  async #load() {
    if (this.#loaded) return;

    const grid = this.querySelector('.apc-collections-highlight__grid');
    const handles = (this.dataset.handles || '')
      .split(',')
      .map((handle) => handle.trim())
      .filter(Boolean);

    if (!grid || handles.length === 0) return;

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    const searchUrl = this.dataset.searchUrl || Theme.routes.search_url;
    const url = new URL(searchUrl, location.origin);
    url.searchParams.set('view', 'apc-collections-highlight');
    url.searchParams.set('q', handles.join(','));

    try {
      const response = await fetch(url.toString(), { signal: this.#activeFetch.signal });
      if (!response.ok) throw new Error(`Server returned ${response.status}`);

      const html = (await response.text()).trim();
      if (!html) {
        this.#handleEmpty();
        return;
      }

      this.#loaded = true;
      // Server-rendered, same-origin HTML from Shopify's search endpoint.
      grid.innerHTML = html;
      grid.removeAttribute('data-loading');
      this.removeAttribute('data-loading');
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      this.#handleError(error);
    } finally {
      this.#activeFetch = null;
    }
  }

  #handleEmpty() {
    this.classList.add('hidden');
    this.dataset.empty = 'true';
  }

  /** @param {unknown} error */
  #handleError(error) {
    console.error('[apc-collections-highlight]', error);
    // Leave skeletons in place rather than hiding a configured section.
  }
}

if (!customElements.get('apc-collections-highlight')) {
  customElements.define('apc-collections-highlight', ApcCollectionsHighlight);
}
